// ---------------------------------------------------------------------------
// Accessify – Text Simplifier pilot telemetry.
// ---------------------------------------------------------------------------
// Numeric-only counters that report manifest hits, phase distributions, and
// persist outcomes back to the worker via `sendBeacon` on `pagehide`. No PII:
// the `contentHash` is a DJB2 of `window.location.pathname`, never the URL or
// page text itself.
//
// Opt-in is per-site (controlled via the manifest response's
// `telemetryEnabled`). Without it the beacon never fires — the run is still
// tracked locally so the call sites can keep their `tel.run?.foo++` style
// without a runtime branch.
// ---------------------------------------------------------------------------

import type { SimplifyLevel } from './text-simplify-types';

export interface TelemetryRun {
  contentHash: string;
  manifestHit: boolean;
  manifestBlocks: number;
  domQualifying: number;
  phase1Applied: number;
  phase1Stale: number;
  phase15IdbHits: number;
  phase2AiCalls: number;
  phase2AiErrors: number;
  persistAttempts: number;
  persistSuccesses: number;
  manifestLatencyMs: number | null;
  sent: boolean; // guards against double-send
}

export interface Telemetry {
  /** Install a fresh run object at the start of a simplify pass. */
  start(domQualifying: number): void;
  /** Send the pagehide beacon if telemetry is enabled AND we have a run.
   *  Safe to call multiple times — guarded by the `sent` flag. */
  beacon(opts: { siteKey: string; base: string; variant: SimplifyLevel }): void;
  /** Current run, mutable. Phase counters are bumped directly. */
  readonly run: TelemetryRun | null;
}

export function createTelemetry(
  getEnabled: () => boolean,
  hashFn: (s: string) => string,
): Telemetry {
  let current: TelemetryRun | null = null;

  return {
    start(domQualifying) {
      current = {
        contentHash: hashFn(window.location.pathname || '/'),
        manifestHit: false,
        manifestBlocks: 0,
        domQualifying,
        phase1Applied: 0,
        phase1Stale: 0,
        phase15IdbHits: 0,
        phase2AiCalls: 0,
        phase2AiErrors: 0,
        persistAttempts: 0,
        persistSuccesses: 0,
        manifestLatencyMs: null,
        sent: false,
      };
    },

    beacon({ siteKey, base, variant }) {
      if (!getEnabled() || !current || current.sent) return;
      const t = current;
      t.sent = true;
      const payload = JSON.stringify({
        siteKey,
        variant,
        contentHash: t.contentHash,
        manifestHit: t.manifestHit,
        manifestBlocks: t.manifestBlocks,
        domQualifying: t.domQualifying,
        phase1Applied: t.phase1Applied,
        phase1Stale: t.phase1Stale,
        phase15IdbHits: t.phase15IdbHits,
        phase2AiCalls: t.phase2AiCalls,
        phase2AiErrors: t.phase2AiErrors,
        persistAttempts: t.persistAttempts,
        persistSuccesses: t.persistSuccesses,
        manifestLatencyMs: t.manifestLatencyMs,
      });
      try {
        const ok = typeof navigator !== 'undefined' && navigator.sendBeacon
          ? navigator.sendBeacon(
              `${base}/v1/telemetry`,
              new Blob([payload], { type: 'application/json' }),
            )
          : false;
        if (!ok) {
          fetch(`${base}/v1/telemetry`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true,
          }).catch(() => {
            /* best-effort — beacon is not load-bearing */
          });
        }
      } catch {
        /* best-effort */
      }
    },

    get run() {
      return current;
    },
  };
}
